import { useRef } from 'react';
import useCustomDispatch, { CustomDispatchProps } from 'redux/dispatch';
import { AccessRule, getAccess, useResolvedAccessContext } from 'access';

export type GuardedDispatchProps = CustomDispatchProps & {
  /**
   * The access required to perform this dispatch. When the current user does not
   * have `canView` for this rule, the underlying action is never dispatched:
   * no request is sent and no success/error message is shown. `onFinally` still
   * runs so callers can clear loading state.
   *
   * Use the same `rule` that guards the corresponding UI (e.g. the
   * `<AccessGuard rule={...}>` wrapping the page), so that hiding a
   * page/section and blocking its requests always stay in sync.
   */
  rule?: AccessRule;
};

/**
 * Permission-aware wrapper around {@link useCustomDispatch}.
 *
 * Wrapping UI in `AccessGuard` hides it, but it does NOT stop data-fetching
 * effects from firing: the effect runs in the component that mounts regardless
 * of the guard, so a `GET` can still be sent for data the user is not allowed to
 * see. Tagging the dispatch with `rule` closes that gap — the request only
 * goes out when the user can actually view the data.
 *
 * This is intentionally generic: it works for any entity (api keys, users,
 * tenants, ...) — just pass the matching `rule`.
 *
 * @example
 * const guardedDispatch = useGuardedDispatch();
 * useEffect(() => {
 *   guardedDispatch({ action: getAPIKeys, rule: rules.apiKey.read });
 * }, [guardedDispatch]);
 */
export const useGuardedDispatch = () => {
  const customDispatch = useCustomDispatch();
  const accessContext = useResolvedAccessContext();
  const accessContextRef = useRef(accessContext);
  accessContextRef.current = accessContext;

  return useRef(({ rule, ...props }: GuardedDispatchProps) => {
    if (rule && !getAccess({ ...accessContextRef.current, rule }).canView) {
      props.onFinally?.();
      return Promise.resolve();
    }

    return customDispatch(props);
  }).current;
};

export default useGuardedDispatch;
